import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { RootState } from "@/app/rootReducer";
import {
  fetchEditCompanyDetailsAction,
  editCompanyAction,
} from "../../slices/companiesSlice";
import { useNotification } from "@/context/NotificationContext";
import { editCompanySchema, type EditCompanyFormData } from "../../schemas/companySchemas";
import { EditCompanyDTO } from "@/api/editCompany/types";
import { CompanyDetailsSection } from "../CompanyDetailsSection/CompanyDetailsSection";
import { SoftwareSection } from "../SoftwareSection/SoftwareSection";
import { TradesSection } from "../TradesSection/TradesSection";
import ServiceTitanIntegration from "../ServiceTitanIntegration/ServiceTitanIntegration";

const EditCompany: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, selectedCompany, tradeList, companyTradeIds } =
    useSelector((state: RootState) => state.companies);
  const { showNotification } = useNotification();

  const form = useForm<EditCompanyFormData>({
    resolver: zodResolver(editCompanySchema),
    defaultValues: {
      companyName: "",
      companyEmail: "",
      websiteUrl: "",
      salesforceId: "",
      intacctId: "",
      marketingEnabled: false,
      hasEaglesAccess: false,
      hasLATAAccess: false,
      fieldServiceSoftwareId: null,
      tradeIds: [],
    },
    mode: "onChange",
  });

  const { setValue, handleSubmit, formState, reset, control } = form;
  const { isSubmitting, isValid, isDirty } = formState;

  // Watch the fieldServiceSoftwareId to determine if ServiceTitan section should be shown
  const selectedSoftwareId = useWatch({ control, name: "fieldServiceSoftwareId" });

  // Determine if ServiceTitan is selected
  const isServiceTitanSelected = useMemo(() => {
    if (!selectedSoftwareId || !selectedCompany?.fieldServiceSoftwareList) {
      return false;
    }
    const selectedSoftware = selectedCompany.fieldServiceSoftwareList.find(
      (software) => software.id === selectedSoftwareId
    );
    return selectedSoftware?.name === "ServiceTitan";
  }, [selectedSoftwareId, selectedCompany?.fieldServiceSoftwareList]);

  useEffect(() => {
    if (uuid) {
      dispatch(fetchEditCompanyDetailsAction(uuid));
    }
  }, [dispatch, uuid]);

  useEffect(() => {
    if (selectedCompany) {
      // Use reset to properly set initial form values
      reset({
        companyName: selectedCompany.companyName,
        companyEmail: selectedCompany.companyEmail || "",
        websiteUrl: selectedCompany.websiteUrl || "",
        salesforceId: selectedCompany.salesforceId || "",
        intacctId: selectedCompany.intacctId || "",
        marketingEnabled: selectedCompany.marketingEnabled || false,
        hasEaglesAccess: selectedCompany.hasEaglesAccess || false,
        hasLATAAccess: selectedCompany.hasLATAAccess || false,
        fieldServiceSoftwareId: selectedCompany.fieldServiceSoftwareId || null,
        tradeIds: companyTradeIds || [],
      });
    }
  }, [selectedCompany, companyTradeIds, reset]);

  const onSubmit = async (values: EditCompanyFormData) => {
    if (!uuid) return;

    try {
      // Update all company details in a single call
      const editCompanyDTO: EditCompanyDTO = {
        companyName: values.companyName,
        companyEmail: values.companyEmail || null,
        websiteUrl: values.websiteUrl || null,
        salesforceId: values.salesforceId || null,
        intacctId: values.intacctId || null,
        marketingEnabled: values.marketingEnabled,
        hasEaglesAccess: values.hasEaglesAccess,
        hasLATAAccess: values.hasLATAAccess,
        fieldServiceSoftwareId: values.fieldServiceSoftwareId ?? null,
        tradeIds: values.tradeIds || [],
      };

      await dispatch(
        editCompanyAction(uuid, editCompanyDTO, (updatedCompany) => {
          console.log("Company updated:", updatedCompany);
        })
      );

      showNotification(
        "Company Updated",
        "All company details have been saved successfully.",
        "success"
      );

      // Reset form to mark it as pristine after successful save
      reset({
        companyName: values.companyName,
        companyEmail: values.companyEmail,
        websiteUrl: values.websiteUrl,
        salesforceId: values.salesforceId,
        intacctId: values.intacctId,
        marketingEnabled: values.marketingEnabled,
        hasEaglesAccess: values.hasEaglesAccess,
        hasLATAAccess: values.hasLATAAccess,
        fieldServiceSoftwareId: values.fieldServiceSoftwareId,
        tradeIds: values.tradeIds,
      });
    } catch (error) {
      showNotification(
        "Update Failed",
        "Failed to update company details. Please try again.",
        "error"
      );
    }
  };

  if (!uuid) {
    return null;
  }

  return (
    <MainPageWrapper
      error={error}
      loading={loading}
      title="Edit Company"
    >
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Company Details Card */}
          <Card>
            <CardHeader>
              <CardTitle>Company Details</CardTitle>
            </CardHeader>
            <CardContent>
              <CompanyDetailsSection form={form} />
            </CardContent>
          </Card>

          {/* Software Configuration Card */}
          <Card>
            <CardHeader>
              <CardTitle>Software Configuration</CardTitle>
            </CardHeader>
            <CardContent>
              <SoftwareSection
                form={form}
                fieldServiceSoftwareList={selectedCompany?.fieldServiceSoftwareList || []}
              />
            </CardContent>
          </Card>

          {/* Trade Access Card */}
          <Card>
            <CardHeader>
              <CardTitle>Trade Access</CardTitle>
            </CardHeader>
            <CardContent>
              <TradesSection
                form={form}
                tradeList={tradeList}
              />
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/admin/companies")}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!isValid || !isDirty || isSubmitting}>
              {isSubmitting ? "Saving..." : "Save All Changes"}
            </Button>
          </div>
        </form>
      </Form>

      {/* ServiceTitan Integration - Separate from main form */}
      {/* Only show if ServiceTitan is selected in the software dropdown */}
      {selectedCompany && uuid && isServiceTitanSelected && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>ServiceTitan Integration</CardTitle>
            <CardDescription>
              Configure ServiceTitan credentials and map business units to trades
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ServiceTitanIntegration
              companyUuid={uuid}
              companyId={selectedCompany.id}
            />
          </CardContent>
        </Card>
      )}
    </MainPageWrapper>
  );
};

export default EditCompany;